﻿import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Chart from 'react-apexcharts';
import useSound from 'use-sound';

import Content_Container from '@Elements/Content_Container';
import { Pomodoro_Button } from '@Elements/Buttons';
import notificationBip from '@Sounds/Notification-Bip.mp3';

import {
    Icon_Play,
    Icon_Pause,
    Icon_Reset
} from '@Elements/ImageElements/Icons';

const Pomodoro = (): JSX.Element => {
    const [t] = useTranslation();
    const default_pomodoro_timer_minutes: number = 25;
    const default_pomodoro_timer_seconds: number = 0;

    const [minutes, set_minutes] = useState<number>(default_pomodoro_timer_minutes);
    const [seconds, set_seconds] = useState<number>(default_pomodoro_timer_seconds);
    const [is_running, set_is_running] = useState<boolean>(false);
    const [play] = useSound(notificationBip);

    useEffect(() => {
        const ongoing_session = sessionStorage.getItem('pomodoroTimer');

        if (ongoing_session) {
            const parsed_session = JSON.parse(ongoing_session);

            set_minutes(parsed_session.minutes);
            set_seconds(parsed_session.seconds);
            set_is_running(parsed_session.is_running);
        }
    }, []);

    useEffect(() => {
        const pomodoro_timer_id = setInterval(() => {
            if (is_running) {
                if (seconds === 0) {
                    if (minutes === 0) {
                        clearInterval(pomodoro_timer_id);
                        set_is_running(false);
                        play();
                        
                        setTimeout(function () {
                            sessionStorage.removeItem('pomodoroTimer');
                            set_minutes(default_pomodoro_timer_minutes);
                            set_seconds(default_pomodoro_timer_seconds);
                        }, 1500);
                    } else {
                        set_minutes(minutes - 1);
                        set_seconds(59);
                    }
                } else {
                    set_seconds(seconds - 1);
                }
            }
        }, 1000);

        return () => clearInterval(pomodoro_timer_id);
    }, [is_running, minutes, seconds]);

    useEffect(() => {
        if (is_running) {
            save_pomodoro_settings(minutes, seconds, is_running);
        }
    }, [minutes, seconds, is_running]);

    function start_timer(): void {
        set_is_running(true);
    }

    function pause_timer(): void {
        set_is_running(false);
        save_pomodoro_settings(minutes, seconds, false);
    }

    function reset_timer(): void {
        set_minutes(default_pomodoro_timer_minutes);
        set_seconds(default_pomodoro_timer_seconds);
        set_is_running(false);

        sessionStorage.removeItem('pomodoroTimer');
    }

    function save_pomodoro_settings(minutes: number, seconds: number, is_running: boolean): void {
        sessionStorage.setItem('pomodoroTimer', JSON.stringify({
            minutes: minutes,
            seconds: seconds,
            is_running: is_running
        }));
    }

    function render_pomodoro_chart_text(): JSX.Element {
        return (
            <div className="Pomodoro_Timer_Text">
                <p className="Pomodoro_Timer_Text_TimeLeft">{t('Text.TimeLeftToBreak')}</p>
                <p className="Pomodoro_Timer_Text_Value">{minutes < 10 ? `0${minutes}` : minutes}:{seconds < 10 ? `0${seconds}` : seconds}</p>
            </div>
        )
    }

    function render_pomodoro_control_buttons(): JSX.Element {
        return (
            <div className="Pomodoro_Timer_Buttons">
                {
                    !is_running ?
                        <Pomodoro_Button
                            onClick={start_timer}
                            aria_name={t('Button.Start')}
                        >
                            <Icon_Play />
                        </Pomodoro_Button> :
                        <Pomodoro_Button
                            onClick={pause_timer}
                            aria_name={t('Button.Pause')}
                        >
                            <Icon_Pause />
                        </Pomodoro_Button>
                }
                <Pomodoro_Button
                    onClick={reset_timer}
                    aria_name={t('Button.Reset')}
                >
                    <Icon_Reset />
                </Pomodoro_Button>
            </div>
        )
    }

    return (
        <section className="Module Pomodoro">
            <header className="Module_Header">
                <h2 className="Headline2">{t('Text.PomodoroTimer')}</h2>
            </header>
            <Content_Container>
                <div className="Pomodoro_Timer">
                    <Chart
                        type="donut"
                        series={[minutes * 60 + seconds, (default_pomodoro_timer_minutes * 60) + default_pomodoro_timer_seconds - (minutes * 60 + seconds)]}
                        options={{
                            chart: {
                                type: 'donut'
                            },
                            dataLabels: {
                                enabled: false
                            },
                            legend: {
                                show: false
                            },
                            colors: ["#44abe1", "#282C32"],
                            labels: [t('Text.TimeLeft'), t('Text.TimePassed')],
                            stroke: {
                                show: false
                            },
                            tooltip: {
                                enabled: false
                            }
                        }}
                    />
                    {render_pomodoro_chart_text()}
                    {render_pomodoro_control_buttons()}
                </div>
            </Content_Container>
        </section>
    )
}

export default React.memo(Pomodoro);